import React, { useState, useEffect, useRef, useCallback } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { INR, initials, today, uuid, invoiceStatus, dateInRange, logAudit, notify, notifyCompanyUsers, isTaskOverdue, getLatestSubmission, submissionNeedsReview } from '../../core/helpers';
import { sb } from '../../core/db';
import { KRAYA } from '../../core/constants';
import { TASK_STATUSES, TASK_REQUIREMENT_TYPES } from '../../core/constants';
import { resolveDocTemplate } from '../../core/seed-doc-templates';
import { Badge } from '../../components/Badge';
import { FilterBar } from '../../components/FilterBar';
import { StatusFilter } from '../../components/StatusFilter';
import { PeriodFilter, usePeriodRange } from '../../components/PeriodFilter';
import { Icon, InlineIcon } from '../../components/Icon';
import { LoadMoreBar } from '../../components/LoadMoreBar';
import { listClientDocs, uploadClientDoc, resolveDocSrc } from '../../storage';
import { buildApiFilter, usePaginatedApiRows } from '../../core/searchFilters';
import { buildOrderParam, sortRows, useTableTools, TABLE_PAGE_SIZE } from '../../core/tableView';
import { SortableTh } from '../../components/SortableTh';

const TASK_ORDER = { sequence: 'sequence', title: 'title', clientName: 'client_name', dueDate: 'due_date', status: 'status' };
const TASK_ACCESSORS = {
  sequence: (r) => Number(r.sequence) || 0,
  title: (r) => (r.title || '').toLowerCase(),
  clientName: (r) => (r.clientName || '').toLowerCase(),
  dueDate: (r) => (r.dueDate ? new Date(r.dueDate).getTime() : 0),
  status: (r) => r.status || '',
};




function TasksPage() {
  const { tasks, user, clients, employees, openModal, can } = useApp();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [period, setPeriod] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const range = usePeriodRange(period, customFrom, customTo);
  useEffect(() => {
    if (period === "all") { setFrom(""); setTo(""); return; }
    if (period === "custom") return;
    const fmt = (d) => d ? d.toISOString().split("T")[0] : "";
    setFrom(fmt(range.start));
    setTo(fmt(range.end));
  }, [period, customFrom, customTo, range.start, range.end]);
  const [sortKey, setSortKey] = useState("dueDate");
  const [sortDir, setSortDir] = useState("asc");
  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const localBaseTasks = tasks.filter(t => !t.isRecurring).filter(t => { // #38 exclude recurring — they live in Recurring Tasks
    if (clientId && t.clientId !== clientId) return false;
    if (search && !((t.title||"").toLowerCase().includes(search.toLowerCase()) || (t.clientName||"").toLowerCase().includes(search.toLowerCase()))) return false;
    if (!dateInRange(t.dueDate, from, to)) return false;
    return true;
  });
  const apiFilter = buildApiFilter({
    equals: {
      ...(clientId || search || from || to ? { is_recurring: "false" } : {}),
      ...(clientId ? { company_id: clientId } : {}),
    },
    from, to,
    dateField: "due_date",
    search,
    searchFields: ["title", "client_name"],
    order: buildOrderParam(sortKey, sortDir, TASK_ORDER) || "due_date.asc",
  });
  const { rows: baseTasks, hasMore, loadMore, loadingMore } = usePaginatedApiRows("tasks", apiFilter, localBaseTasks, { enabled: !!apiFilter });
  const clearFilters = () => { setSearch(""); setClientId(""); setFrom(""); setTo(""); setPeriod("all"); setCustomFrom(""); setCustomTo(""); };
  const visible = baseTasks.filter(t => {
    if (user.role === "employee" && t.assignedTo !== user.id) return false;
    if (filter === "all") return true;
    if (filter === "overdue") return isTaskOverdue(t);
    return t.status === filter;
  });

  // #1 Status filter — collapsed from a long wrapping chip row into a single dropdown.
  const statusOptions = [
    { id: "all", label: "All", count: baseTasks.length },
    ...TASK_STATUSES.map(s => ({
      id: s.id,
      label: s.label,
      count: (s.id === "overdue" ? baseTasks.filter(isTaskOverdue) : baseTasks.filter(t => t.status === s.id)).length,
    })),
  ];

  return (
    <>
      {/* #2 Summary cards — 4 in parallel on desktop, clean 2×2 on mobile */}
      <div className="stat-cards">
        {[
          ["Pending",   baseTasks.filter(t=>t.status!=="completed"&&t.status!=="cancelled").length, "var(--orange)"],
          ["Completed", baseTasks.filter(t=>t.status==="completed").length,                         "var(--green)"],
          ["Overdue",   baseTasks.filter(isTaskOverdue).length, "var(--red)"],
          ["Total",     baseTasks.length, "var(--ink)"],
        ].map(([l, v, c]) => (
          <div key={l} className="stat-box" style={{cursor:"default"}}>
            <div className="stat-lbl">{l}</div>
            <div className="stat-val" style={{ fontSize:22,color:c }}>{v}</div>
          </div>
        ))}
      </div>
      <FilterBar
        search={search} setSearch={setSearch} searchPlaceholder="Search task / client…"
        clientId={clientId} setClientId={isAdmin ? setClientId : undefined} clients={clients}
        from={from} setFrom={setFrom} to={to} setTo={setTo} onClear={clearFilters}
        extra={<PeriodFilter period={period} setPeriod={setPeriod} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} style={{ marginBottom: 0 }} />}
      />
      <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
        <StatusFilter value={filter} onChange={setFilter} options={statusOptions} label="Status" />
        {can && can("tasks","create") && (
          <button className="btn btn-primary" style={{ marginLeft:"auto" }} onClick={()=>openModal("add-task", clientId ? { clientId } : {})}>+ New Task</button>
        )}
      </div>
      <TasksTable
        tasks={visible}
        showClient={true}
        showEdit={isAdmin}
        hasMore={hasMore}
        loadMore={loadMore}
        loadingMore={loadingMore}
        shownCount={baseTasks.length}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        apiSorted
      />
    </>
  );
}

function TasksTable({
  tasks, showClient = true, showEdit = false, hasMore, loadMore, loadingMore, shownCount,
  sortKey: controlledSortKey, sortDir: controlledSortDir, onSort, apiSorted = false,
  localPageSize = TABLE_PAGE_SIZE,
}) {
  const { openModal, setTasks, dbSetTasks, submissions, user, clients, users, userLinks, can, docTpls, showToast } = useApp();
  const isTeam = user?.role === "admin" || user?.role === "manager" || user?.role === "employee";
  const isApiPaged = hasMore != null || loadMore != null;
  const local = useTableTools(tasks, {
    pageSize: localPageSize,
    defaultSort: { key: 'dueDate', dir: 'asc' },
    accessors: TASK_ACCESSORS,
    apiPaged: apiSorted || isApiPaged,
  });
  const sortKey = controlledSortKey ?? local.sortKey;
  const sortDir = controlledSortDir ?? local.sortDir;
  const toggleSort = onSort ?? local.toggleSort;
  const displayTasks = (apiSorted || isApiPaged)
    ? (apiSorted ? tasks : sortRows(tasks, sortKey, sortDir, TASK_ACCESSORS))
    : local.visible;
  const showLoadMore = isApiPaged ? hasMore : local.hasMore;
  const onLoadMore = isApiPaged ? loadMore : local.loadMore;
  const loadMoreCount = isApiPaged ? shownCount : local.shownCount;
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [submittingDelete, setSubmittingDelete] = useState(false);

  const submitDeleteRequest = async () => {
    if (!confirmDelete) return;
    setSubmittingDelete(true);
    try {
      await sb.createDeleteRequest({
        entity_type: "tasks",
        entity_id: confirmDelete.id,
        reason: deleteReason.trim(),
        company_id: confirmDelete.clientId,
        reassign_to: null,
      });
      showToast("Delete request submitted — awaiting admin approval", "success");
      setConfirmDelete(null);
      setDeleteReason("");
    } catch (err) {
      showToast(err?.message || "Failed to submit delete request", "error");
    } finally {
      setSubmittingDelete(false);
    }
  };

  const updateStatus = async (taskId, newStatus) => {
    let changedTask = null;
    // Update local state immediately
    setTasks(ts => ts.map(t => {
      if (t.id !== taskId) return t;
      const updated = { ...t, status: newStatus, completedDate: newStatus === "completed" ? today() : t.completedDate };
      changedTask = updated;
      const client = clients.find(c => c.id === t.clientId);
      if (newStatus === "client_action" && client?.phone) KRAYA.taskActionNeeded(client.phone, client.contactName || client.name, t.title);
      if (newStatus === "completed" && client?.phone) KRAYA.taskCompleted(client.phone, t.title, client.name);
      return updated;
    }));
    // Save to DB
    await sb.update("tasks", "id", taskId, {
      status:         newStatus,
      completed_date: newStatus === "completed" ? new Date().toISOString().split("T")[0] : null,
      updated_at:     new Date().toISOString(),
    });
    if (changedTask && newStatus === "client_action") {
      await notifyCompanyUsers(changedTask.clientId, { userLinks, users, clients }, {
        type: "task",
        title: "Action required on task",
        body: `Please complete: ${changedTask.title}`,
        meta: { taskId: changedTask.id, action: "submit", modal: "client-submit-task" },
      });
    }
  };

  return (
    <div className="card">
      <div className="table-scroll-shell">
        <div className="tbl-wrap tbl-m-tasks">
          <table>
          <thead>
            <tr>
              <SortableTh label="#" sortKey="sequence" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Task" sortKey="title" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              {showClient && <SortableTh label="Client" sortKey="clientName" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
              <SortableTh label="Due Date" sortKey="dueDate" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Status" sortKey="status" activeKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              {isTeam && <th>Submission</th>}
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {displayTasks.length === 0 && (
              <tr><td colSpan={8}><div className="empty"><div className="empty-icon"><Icon name="checklist" size={30} /></div><div>No tasks found</div></div></td></tr>
            )}
            {displayTasks.map(t => {
              const isOverdue = t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "completed";
              const sub       = getLatestSubmission(submissions, t.id);
              const hasPendingReview = submissionNeedsReview(sub);
              return (
                <tr key={t.id}>
                  <td style={{ fontSize:11, color:"var(--muted)", fontWeight:700 }}>#{t.sequence}</td>
                  <td>
                    <div style={{ fontWeight:500, fontSize:13 }}>{t.title}</div>
                    {t.docTemplateId && (() => {
                      const dtpl = resolveDocTemplate(t.docTemplateId, docTpls) || {};
                      return (
                      <div style={{ fontSize:10, color:"var(--blue)", marginTop:2, display:"inline-flex", alignItems:"center", gap:4 }}>
                        <Icon name="clipboard" size={11} />
                        {dtpl.name || t.docTemplateId}
                        {" · "}{dtpl.collectionType==="director" ? "Per Director":"Company"}
                      </div>
                      );
                    })()}
                    {t.notes && <div style={{ fontSize:11, color:"var(--muted)" }}>{t.notes}</div>}
                  </td>
                  {showClient && <td style={{ fontSize:12, color:"var(--muted)", maxWidth:140 }}>{t.clientName}</td>}
                  <td>
                    <span style={{ fontSize:12, fontWeight:600, color:isOverdue?"var(--red)":"var(--ink)", background:isOverdue?"#FEF2F2":"transparent", padding:isOverdue?"2px 7px":0, borderRadius:5, display:"inline-flex", alignItems:"center", gap:3 }}>
                      {t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-IN",{day:"numeric",month:"short"}) : "—"}
                      {isOverdue && <Icon name="warning" size={12} color="var(--red)" />}
                    </span>
                  </td>
                  <td>
                    {isTeam ? (
                      <select value={t.status} onChange={e=>updateStatus(t.id,e.target.value)}
                        style={{ fontSize:11, padding:"4px 8px", border:"1.5px solid var(--border)", borderRadius:6, cursor:"pointer", background:"#fff" }}>
                        {TASK_STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    ) : (
                      <Badge status={t.status} />
                    )}
                  </td>
                  {isTeam && (
                    <td>
                      {hasPendingReview ? (
                        <button className="btn btn-sm" style={{ background:"#7C3AED",color:"#fff",border:"none" }}
                          onClick={()=>openModal("review-submission",{submissionId:sub.id})}>
                          <InlineIcon name="review" size={13} gap={5}>Review</InlineIcon>
                        </button>
                      ) : sub?.status === "approved" ? (
                        <span style={{ fontSize:11,color:"var(--green)",fontWeight:600,display:"inline-flex",alignItems:"center",gap:4 }}><Icon name="check" size={12} />Approved</span>
                      ) : sub?.status === "changes_requested" ? (
                        <span style={{ fontSize:11,color:"var(--red)",fontWeight:600,display:"inline-flex",alignItems:"center",gap:4 }}><Icon name="undo" size={12} />Changes sent</span>
                      ) : t.requirementType !== "none" && t.docTemplateId ? (
                        <span style={{ fontSize:11,color:"var(--muted)" }}>Awaiting client</span>
                      ) : (
                        <span style={{ fontSize:11,color:"var(--faint)" }}>—</span>
                      )}
                    </td>
                  )}
                  <td style={{display:"flex",gap:6,alignItems:"center"}}>
                    <button className="btn btn-sm" onClick={()=>openModal("view-task",{task:t})}>View</button>
                    {isTeam && can && can("tasks","edit") && (
                      <button className="btn btn-sm" onClick={()=>openModal("edit-task",{task:t})}><InlineIcon name="edit" size={13} gap={4}>Edit</InlineIcon></button>
                    )}
                    {isTeam && can && can("tasks","delete") && (
                      <button className="btn btn-sm" style={{background:"#FEF2F2",color:"var(--red)",border:"1px solid #FCA5A5"}} onClick={()=>{ setConfirmDelete(t); setDeleteReason(""); }}>
                        <InlineIcon name="trash" size={13} gap={4}>Delete</InlineIcon>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      {confirmDelete && (
        <div className="dr-confirm-form" style={{ padding:"16px 20px", background:"#FEF2F2", borderTop:"1px solid #FCA5A5", borderBottom:"1px solid #FCA5A5", margin:"0 -20px 16px" }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#991B1B", marginBottom:6 }}>Delete "{confirmDelete.title}"?</div>
          <div style={{ fontSize:12, color:"#991B1B", marginBottom:10 }}>This requires admin approval before taking effect.</div>
          <div className="f-group" style={{ marginBottom:10 }}>
            <label className="f-label">Reason for deletion (optional)</label>
            <textarea className="f-textarea" rows={2} value={deleteReason} onChange={e=>setDeleteReason(e.target.value)} placeholder="Add a reason for this deletion request…" />
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-sm" onClick={()=>{ setConfirmDelete(null); setDeleteReason(""); }} disabled={submittingDelete}>Cancel</button>
            <button className="btn btn-sm btn-red" onClick={submitDeleteRequest} disabled={submittingDelete}>
              <InlineIcon name="trash" size={13} gap={5}>{submittingDelete ? "Submitting…" : "Submit Delete Request"}</InlineIcon>
            </button>
          </div>
        </div>
      )}
      <LoadMoreBar hasMore={showLoadMore} loadingMore={loadingMore} onLoadMore={onLoadMore} count={loadMoreCount} />
    </div>
  );
}

export { TasksPage, TasksTable };
